import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import {
  addMonths,
  eachDayOfInterval,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { CalendarDay, CalendarResponse } from '@pnl/shared';
import { api } from '../../lib/api';
import { formatBrl, formatNumber } from '../../lib/format';
import { Card } from './Card';
import { DistributionCharts } from './DistributionCharts';

interface Props {
  accountId: string;
}

type Tab = 'pnl' | 'trades' | 'charts';

const DOW = ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB', 'DOM'];

function dayKey(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

export function TradingJournal({ accountId }: Props) {
  const [tab, setTab] = useState<Tab>('pnl');
  const [cursor, setCursor] = useState<Date>(() => new Date());

  const year = cursor.getUTCFullYear();
  const month = cursor.getUTCMonth() + 1;

  const { data, isLoading } = useQuery<CalendarResponse>({
    queryKey: ['calendar', accountId, year, month],
    queryFn: () => api.getCalendar(accountId, year, month),
  });

  const cells = useMemo(() => {
    const monthStart = startOfMonth(cursor);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const totalDays = 42;
    return eachDayOfInterval({
      start: gridStart,
      end: new Date(gridStart.getTime() + (totalDays - 1) * 24 * 60 * 60 * 1000),
    })
      .slice(0, totalDays)
      .map((date) => ({
        date,
        inMonth: isSameMonth(date, monthStart),
        isToday: isToday(date),
        key: dayKey(date),
      }));
  }, [cursor]);

  const dayLookup = useMemo(() => {
    const m = new Map<string, CalendarDay>();
    data?.days.forEach((d) => m.set(d.date, d));
    return m;
  }, [data]);

  const monthLabel = format(cursor, 'MMMM yyyy', { locale: ptBR });
  const monthLabelCap = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

  return (
    <Card>
      <div className="border-ink-800 border-b px-5 py-3.5">
        <div>
          <p className="text-ink-100 text-sm font-semibold">Diário de Trading</p>
          <div className="text-2xs text-ink-400 mt-2 flex gap-4">
            <button
              type="button"
              onClick={() => setTab('pnl')}
              className={
                'pb-1 font-semibold transition-colors ' +
                (tab === 'pnl'
                  ? 'border-brand-500 text-brand-300 border-b-2'
                  : 'text-ink-500 hover:text-ink-300')
              }
            >
              PnL Diário
            </button>
            <button
              type="button"
              onClick={() => setTab('trades')}
              className={
                'pb-1 font-semibold transition-colors ' +
                (tab === 'trades'
                  ? 'border-brand-500 text-brand-300 border-b-2'
                  : 'text-ink-500 hover:text-ink-300')
              }
            >
              Posições fechadas
            </button>
            <button
              type="button"
              onClick={() => setTab('charts')}
              className={
                'pb-1 font-semibold transition-colors ' +
                (tab === 'charts'
                  ? 'border-brand-500 text-brand-300 border-b-2'
                  : 'text-ink-500 hover:text-ink-300')
              }
            >
              Gráficos
            </button>
          </div>
        </div>
      </div>

      {tab === 'pnl' ? (
        <>
          {/* Nav mês + stats na mesma linha (estilo FTMO) */}
          <div className="border-ink-800 flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCursor(new Date())}
                className="border-ink-700 bg-ink-800 hover:bg-ink-700 text-ink-200 rounded-md border px-3 py-1 text-xs font-medium"
              >
                Hoje
              </button>
              <button
                type="button"
                onClick={() => setCursor((c) => addMonths(c, -1))}
                className="text-ink-300 hover:bg-ink-800 rounded-md p-1.5"
              >
                <ChevronLeft size={14} />
              </button>
              <p className="text-ink-100 min-w-[140px] text-center text-sm font-semibold">
                {monthLabelCap}
              </p>
              <button
                type="button"
                onClick={() => setCursor((c) => addMonths(c, 1))}
                className="text-ink-300 hover:bg-ink-800 rounded-md p-1.5"
              >
                <ChevronRight size={14} />
              </button>
            </div>

            <div className="flex items-center gap-2">
              <span
                className={
                  'text-2xs rounded-md px-2.5 py-1 font-semibold tabular-nums ' +
                  ((data?.monthlyPnlCents ?? 0) >= 0
                    ? 'bg-success-500/15 text-success-300'
                    : 'bg-danger-500/15 text-danger-300')
                }
              >
                Monthly stats: {formatBrl(data?.monthlyPnlCents ?? 0, { signed: true })}
              </span>
              <span className="bg-ink-800 text-ink-300 text-2xs rounded-md px-2.5 py-1 font-medium tabular-nums">
                Trading days: {data?.tradingDays ?? 0}
              </span>
            </div>
          </div>

          {/* Grid */}
          <div className="p-3">
            <div className="text-2xs text-ink-400 grid grid-cols-7 gap-1 px-1 pb-2 font-semibold uppercase tracking-widest">
              {DOW.map((d) => (
                <div key={d} className="text-center">
                  {d}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {cells.map((cell) => {
                const day = dayLookup.get(cell.key);
                const tone =
                  !day || day.pnlCents === 0 ? null : day.pnlCents > 0 ? 'positive' : 'negative';
                // Célula sempre com borda neutra (sem contorno colorido); só a
                // tinta de fundo e o valor é que indicam pos/neg.
                const bgColor =
                  tone === 'positive'
                    ? 'bg-success-500/10 border-ink-700 hover:border-ink-600'
                    : tone === 'negative'
                      ? 'bg-danger-500/10 border-ink-700 hover:border-ink-600'
                      : 'bg-ink-900 border-ink-700 hover:border-ink-600';
                const outOfMonth = !cell.inMonth ? 'opacity-30' : '';
                return (
                  <div
                    key={cell.key}
                    className={
                      'min-h-[80px] rounded-md border p-2 transition-colors ' +
                      bgColor +
                      ' ' +
                      outOfMonth
                    }
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={
                          'text-2xs font-semibold ' +
                          (cell.isToday
                            ? 'bg-brand-500 grid h-5 w-5 place-items-center rounded-full text-white'
                            : 'text-[#A8A8A8]')
                        }
                      >
                        {cell.date.getDate()}
                      </span>
                    </div>
                    {day && day.pnlCents !== 0 && (
                      <div className="mt-2">
                        <p
                          className={
                            'text-sm font-bold tabular-nums ' +
                            (tone === 'positive' ? 'text-success-300' : 'text-danger-300')
                          }
                        >
                          {formatBrl(day.pnlCents, { signed: true })}
                        </p>
                        <p className="text-2xs text-ink-400 mt-0.5">Posições: {day.positions}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {isLoading && (
              <p className="text-ink-400 mt-3 text-center text-xs">Carregando calendário…</p>
            )}

            <p className="text-2xs mt-3 text-center text-[#9A9A9A]">
              As posições atuais serão atualizadas após auditoria diária
            </p>
          </div>
        </>
      ) : (
        /* Altura mínima reservada: ao trocar de aba, o conteúdo novo entra vazio
           (busca própria) e a página encolheria — o scroll subia e voltava ao
           carregar, o "sobe e desce" que o cliente viu. Com o piso de altura o
           layout não colapsa durante a troca. */
        <div className="min-h-[520px]">
          {tab === 'trades' ? (
            <ClosedPositions accountId={accountId} />
          ) : (
            <DistributionCharts accountId={accountId} />
          )}
        </div>
      )}
    </Card>
  );
}

function ClosedPositions({ accountId }: { accountId: string }) {
  const [page, setPage] = useState(1);
  const [side, setSide] = useState<'' | 'BUY' | 'SELL'>('');
  const [symbol, setSymbol] = useState('');
  const [exporting, setExporting] = useState(false);
  const pageSize = 15;

  async function handleExport() {
    setExporting(true);
    try {
      const blob = await api.downloadTradesCsv(accountId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `operacoes-${accountId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Falha ao exportar CSV', err);
    } finally {
      setExporting(false);
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ['trades', accountId, page, side, symbol],
    queryFn: () =>
      api.getAccountTrades(accountId, {
        page,
        pageSize,
        ...(side ? { side } : {}),
        ...(symbol ? { symbol } : {}),
      }),
  });

  return (
    <div>
      <div className="border-ink-800 flex flex-wrap items-center gap-3 border-b px-5 py-3">
        <input
          value={symbol}
          onChange={(e) => {
            setSymbol(e.target.value.toUpperCase());
            setPage(1);
          }}
          placeholder="Filtrar por símbolo (ex: WINM26)"
          className="border-ink-700 bg-ink-800 placeholder:text-ink-500 text-ink-100 focus:border-brand-500 focus:ring-brand-500/20 rounded-md border px-2.5 py-1 text-xs outline-none focus:ring-2"
        />
        <select
          value={side}
          onChange={(e) => {
            setSide(e.target.value as '' | 'BUY' | 'SELL');
            setPage(1);
          }}
          className="border-ink-700 bg-ink-800 text-ink-100 focus:border-brand-500 focus:ring-brand-500/20 rounded-md border px-2 py-1 text-xs outline-none focus:ring-2"
        >
          <option value="">Todos os lados</option>
          <option value="BUY">BUY</option>
          <option value="SELL">SELL</option>
        </select>
        {(symbol || side) && (
          <button
            type="button"
            onClick={() => {
              setSymbol('');
              setSide('');
              setPage(1);
            }}
            className="text-ink-400 hover:text-ink-200 text-xs underline"
          >
            limpar
          </button>
        )}
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          className="border-ink-700 bg-ink-800 hover:bg-ink-700 text-ink-200 ml-auto inline-flex items-center gap-1.5 rounded-md border px-3 py-1 text-xs font-medium disabled:opacity-50"
        >
          <Download size={13} />
          {exporting ? 'Exportando…' : 'Exportar CSV'}
        </button>
      </div>

      {isLoading ? (
        <p className="text-ink-400 px-5 py-10 text-center text-sm">Carregando operações…</p>
      ) : data && data.items.length === 0 ? (
        <p className="text-ink-400 px-5 py-10 text-center text-sm">
          Nenhuma operação no período. Aparecem aqui após cada importação do CSV pelo time.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-ink-800 text-2xs text-ink-400 border-b text-left font-semibold uppercase tracking-widest">
                  <th className="px-5 py-2.5">Data / Hora</th>
                  <th className="px-2 py-2.5">Símbolo</th>
                  <th className="px-2 py-2.5">Lado</th>
                  <th className="px-2 py-2.5 text-right">Qtd</th>
                  <th className="px-2 py-2.5 text-right">Preço</th>
                  <th className="px-5 py-2.5 text-right">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {data!.items.map((t) => (
                  <tr key={t.id} className="border-ink-800 border-b last:border-b-0">
                    <td className="text-ink-200 px-5 py-2.5 text-xs tabular-nums">
                      {new Date(t.tradeDatetime).toLocaleString('pt-BR')}
                    </td>
                    <td className="text-ink-100 px-2 py-2.5 font-semibold">{t.symbol}</td>
                    <td className="px-2 py-2.5">
                      <span
                        className={
                          'text-2xs rounded px-1.5 py-0.5 font-semibold ' +
                          (t.side === 'BUY'
                            ? 'bg-success-500/15 text-success-300'
                            : 'bg-danger-500/15 text-danger-300')
                        }
                      >
                        {t.side}
                      </span>
                    </td>
                    <td className="text-ink-200 px-2 py-2.5 text-right tabular-nums">{t.qty}</td>
                    <td className="text-ink-200 px-2 py-2.5 text-right tabular-nums">
                      {formatNumber(t.price, 2)}
                    </td>
                    <td
                      className={
                        'px-5 py-2.5 text-right font-semibold tabular-nums ' +
                        (t.pnlCents > 0
                          ? 'text-success-300'
                          : t.pnlCents < 0
                            ? 'text-danger-300'
                            : 'text-ink-200')
                      }
                    >
                      {formatBrl(t.pnlCents, { signed: true })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border-ink-800 flex items-center justify-between border-t px-5 py-3">
            <span className="text-2xs text-ink-400 tabular-nums">
              {data!.total} operações · página {page}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="border-ink-700 bg-ink-800 text-ink-200 rounded-md border px-3 py-1 text-xs font-medium disabled:opacity-40"
              >
                Anterior
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                disabled={page * pageSize >= data!.total}
                className="border-ink-700 bg-ink-800 text-ink-200 rounded-md border px-3 py-1 text-xs font-medium disabled:opacity-40"
              >
                Próxima
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
